/**
 * Working Directory Manager
 * 
 * Uses the File System Access API to let users pick local folders.
 * Directory handles are persisted in IndexedDB (browser-only, not sent to server).
 * Only the folder display name is stored in the project record on the server.
 */

const DB_NAME = 'MySpecWorkspace_FS';
const DB_VERSION = 1;
const STORE_NAME = 'directoryHandles';

function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (e) => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'projectId' });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Check if the File System Access API is supported
 */
export function isFileSystemAccessSupported() {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

/**
 * Prompt user to pick a directory. Returns { handle, name } or null.
 */
export async function pickDirectory() {
  if (!isFileSystemAccessSupported()) {
    throw new Error('File System Access API is not supported in this browser. Please use Chrome or Edge.');
  }
  try {
    const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
    return { handle, name: handle.name };
  } catch (err) {
    if (err.name === 'AbortError') return null; // user cancelled
    throw err;
  }
}

/**
 * Save a directory handle to IndexedDB for a project
 */
export async function saveDirectoryHandle(projectId, handle, displayName) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).put({
      projectId,
      handle,
      displayName: displayName || handle.name,
      savedAt: new Date().toISOString(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Retrieve a directory handle from IndexedDB for a project
 */
export async function getDirectoryHandle(projectId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const req = tx.objectStore(STORE_NAME).get(projectId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Remove a directory handle from IndexedDB
 */
export async function removeDirectoryHandle(projectId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    tx.objectStore(STORE_NAME).delete(projectId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Verify that we still have permission to access a directory handle.
 * Returns true if access is granted, false otherwise.
 */
export async function verifyPermission(handle) {
  if (!handle) return false;
  try {
    const opts = { mode: 'readwrite' };
    if ((await handle.queryPermission(opts)) === 'granted') return true;
    if ((await handle.requestPermission(opts)) === 'granted') return true;
    return false;
  } catch {
    return false;
  }
}

/**
 * List files in a directory handle (non-recursive, top level only)
 */
export async function listFiles(handle) {
  if (!handle) return [];
  const files = [];
  try {
    for await (const entry of handle.values()) {
      files.push({
        name: entry.name,
        kind: entry.kind, // 'file' or 'directory'
      });
    }
  } catch {
    return [];
  }
  return files.sort((a, b) => {
    if (a.kind !== b.kind) return a.kind === 'directory' ? -1 : 1;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Read a file from the directory handle
 */
export async function readFile(dirHandle, fileName) {
  try {
    const fileHandle = await dirHandle.getFileHandle(fileName);
    const file = await fileHandle.getFile();
    return await file.text();
  } catch {
    return null;
  }
}

/**
 * Write content to a file in the directory handle
 */
export async function writeFile(dirHandle, fileName, content) {
  try {
    const fileHandle = await dirHandle.getFileHandle(fileName, { create: true });
    const writable = await fileHandle.createWritable();
    await writable.write(content);
    await writable.close();
    return true;
  } catch {
    return false;
  }
}

/**
 * Recursively iterate all files in a directory handle (subfolders included).
 * Yields { entry, path } where path is relative (e.g. "sub/ICN-123.png").
 * Hidden folders (dot-prefixed) are skipped. Depth-limited to avoid runaway trees.
 */
export async function* walkDirectory(dirHandle, maxDepth = 5, _depth = 0, _path = '') {
  for await (const entry of dirHandle.values()) {
    if (entry.kind === 'file') {
      yield { entry, path: _path ? `${_path}/${entry.name}` : entry.name };
    } else if (entry.kind === 'directory' && _depth < maxDepth && !entry.name.startsWith('.')) {
      yield* walkDirectory(entry, maxDepth, _depth + 1, _path ? `${_path}/${entry.name}` : entry.name);
    }
  }
}
